import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { useToastStore } from '../../store/toastStore'
import { translateApiError } from '../../lib/apiErrors'
import AuthBrand from '../../components/auth/AuthBrand'
import AuthShell from '../../components/auth/AuthShell'
import {
  authHeadingClass,
  authInputClass,
  authLabelClass,
  authLinkClass,
  authSubmitClass,
  authSubtextClass,
} from '../../components/auth/authTheme'

export default function ResetPassword() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const { t } = useTranslation(['auth', 'common'])
  const { resetPassword, isLoading } = useAuthStore()
  const { addToast } = useToastStore()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!token) {
      addToast(t('auth:resetPassword.invalidToken'), 'error')
      return
    }

    if (!password.trim()) {
      addToast(t('common:form.passwordRequired'), 'error')
      return
    }

    if (password.length < 8) {
      addToast(t('common:form.passwordTooShort'), 'error')
      return
    }

    if (password !== confirmPassword) {
      addToast(t('common:form.passwordsMismatch'), 'error')
      return
    }

    try {
      await resetPassword(token, password)
      addToast(t('auth:resetPassword.success'), 'success')
      navigate('/auth/login')
    } catch (error) {
      addToast(translateApiError(error, t), 'error')
    }
  }

  if (!token) {
    return (
      <AuthShell>
        <div className="text-center">
          <AuthBrand />
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-900/40 border border-red-700">
            <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className={`mt-6 ${authHeadingClass}`}>{t('auth:resetPassword.invalidLink.heading')}</h2>
          <p className="mt-2 text-sm text-gray-400">{t('auth:resetPassword.invalidLink.body')}</p>
          <div className="mt-6">
            <Link to="/auth/forgot-password" className={authLinkClass}>
              {t('auth:resetPassword.invalidLink.action')}
            </Link>
          </div>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <div>
        <AuthBrand />
        <h2 className={authHeadingClass}>{t('auth:resetPassword.heading')}</h2>
        <p className={authSubtextClass}>{t('auth:resetPassword.description')}</p>
      </div>

      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label htmlFor="password" className={authLabelClass}>
              {t('auth:resetPassword.newPasswordLabel')}
            </label>
            <div className="relative mt-1">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                minLength={8}
                className={`rounded-md pr-10 ${authInputClass}`}
                placeholder={t('auth:resetPassword.newPasswordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-200"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className={authLabelClass}>
              {t('auth:resetPassword.confirmPasswordLabel')}
            </label>
            <div className="relative mt-1">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                minLength={8}
                className={`rounded-md pr-10 ${authInputClass}`}
                placeholder={t('auth:resetPassword.confirmPasswordPlaceholder')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-200"
                onClick={() => setShowConfirmPassword((v) => !v)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
        </div>

        <div>
          <button type="submit" disabled={isLoading} className={authSubmitClass}>
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#0d0d0d]" />
            ) : (
              t('auth:resetPassword.submitButton')
            )}
          </button>
        </div>

        <div className="text-center">
          <Link to="/auth/login" className={authLinkClass}>
            {t('common:backToLogin')}
          </Link>
        </div>
      </form>
    </AuthShell>
  )
}
